import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { 
  Bot, 
  ArrowLeft, 
  Edit, 
  Trash2, 
  PlayCircle,
  GitBranch,
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle
} from 'lucide-react';
import { adminApi, AgentMetadata, AgentPrompt, AgentDeployment } from '../../services/admin-api';

export function AIDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [agent, setAgent] = useState<AgentMetadata | null>(null);
  const [prompts, setPrompts] = useState<AgentPrompt[]>([]);
  const [deployments, setDeployments] = useState<AgentDeployment[]>([]);
  const [dependencies, setDependencies] = useState<AgentMetadata[]>([]);
  const [dependents, setDependents] = useState<AgentMetadata[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'dependencies' | 'prompts' | 'deployments' | 'config'>('overview');

  useEffect(() => {
    if (id) {
      loadAgentData();
    }
  }, [id]);

  const loadAgentData = async () => {
    if (!id) return;
    
    try {
      setLoading(true);
      const [agentData, promptsData, deploymentsData, depsData, depsentsData] = await Promise.all([
        adminApi.agents.get(id),
        adminApi.prompts.getByAgent(id),
        adminApi.deployments.list(id),
        adminApi.agents.dependencies(id),
        adminApi.agents.dependents(id),
      ]);
      
      setAgent(agentData);
      setPrompts(promptsData.prompts);
      setDeployments(deploymentsData.deployments);
      setDependencies(depsData.dependencies);
      setDependents(depsentsData.dependents);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load agent');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!id || !confirm('Are you sure you want to delete this agent?')) return;
    
    try {
      await adminApi.agents.delete(id);
      navigate('/ai');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete agent');
    }
  };

  const handleTest = async () => {
    if (!id) return;
    
    const input = prompt('Enter test input:');
    if (!input) return;
    
    try {
      const result = await adminApi.agents.test(id, { test: input });
      alert(`Test result:\n${JSON.stringify(result, null, 2)}`);
    } catch (err) {
      alert(`Test failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'failed':
        return <XCircle className="w-5 h-5 text-red-600" />;
      case 'pending':
        return <Clock className="w-5 h-5 text-yellow-600" />;
      default:
        return <AlertCircle className="w-5 h-5 text-gray-600" />;
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-uw-interactive-primary"></div>
          <p className="mt-4 text-uw-text-secondary">Loading agent...</p>
        </div>
      </div>
    );
  }

  if (error || !agent) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-red-800 dark:text-red-200">{error || 'Agent not found'}</p>
        </div>
        <Link
          to="/ai"
          className="inline-flex items-center mt-4 text-uw-interactive-primary hover:text-uw-interactive-primary-hover"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Agents
        </Link>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-6">
        <Link
          to="/ai"
          className="inline-flex items-center text-uw-interactive-primary hover:text-uw-interactive-primary-hover mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Agents
        </Link>
        
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-4">
            <Bot className="w-12 h-12 text-uw-interactive-primary mt-1" />
            <div>
              <h1 className="text-3xl font-bold text-uw-text-primary">{agent.name}</h1>
              <p className="text-uw-text-secondary mt-1">{agent.description}</p>
              <div className="flex gap-2 mt-3">
                <span className="px-3 py-1 bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200 rounded text-sm font-medium">
                  {agent.type}
                </span>
                <span className={`px-3 py-1 rounded text-sm font-medium ${
                  agent.deploymentStatus === 'production'
                    ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                    : agent.deploymentStatus === 'staging'
                    ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                    : 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                }`}>
                  {agent.deploymentStatus}
                </span>
              </div>
            </div>
          </div>
          
          <div className="flex gap-2">
            <button
              onClick={handleTest}
              className="inline-flex items-center px-4 py-2 bg-uw-surface-elevated text-uw-text-primary border border-uw-border-default rounded-md hover:bg-uw-surface-hover transition-colors"
            >
              <PlayCircle className="w-4 h-4 mr-2" />
              Test
            </button>
            <button
              onClick={() => {}}
              className="inline-flex items-center px-4 py-2 bg-uw-interactive-primary text-white rounded-md hover:bg-uw-interactive-primary-hover transition-colors"
            >
              <Edit className="w-4 h-4 mr-2" />
              Edit
            </button>
            <button
              onClick={handleDelete}
              className="inline-flex items-center px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-uw-border-default mb-6">
        <nav className="flex gap-6">
          {['overview', 'dependencies', 'prompts', 'deployments', 'config'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab as any)}
              className={`pb-3 px-1 border-b-2 transition-colors capitalize ${
                activeTab === tab
                  ? 'border-uw-interactive-primary text-uw-interactive-primary'
                  : 'border-transparent text-uw-text-secondary hover:text-uw-text-primary'
              }`}
            >
              {tab}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-uw-surface-default rounded-lg shadow border border-uw-border-default p-6">
            <h2 className="text-xl font-semibold text-uw-text-primary mb-4">Basic Information</h2>
            <dl className="space-y-3">
              <div>
                <dt className="text-sm font-medium text-uw-text-secondary">Function Name</dt>
                <dd className="text-sm text-uw-text-primary font-mono">{agent.functionName}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-uw-text-secondary">Type</dt>
                <dd className="text-sm text-uw-text-primary">{agent.type}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-uw-text-secondary">Deployment Status</dt>
                <dd className="text-sm text-uw-text-primary">{agent.deploymentStatus}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-uw-text-secondary">Created</dt>
                <dd className="text-sm text-uw-text-primary">{new Date(agent.createdAt).toLocaleString()}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-uw-text-secondary">Last Updated</dt>
                <dd className="text-sm text-uw-text-primary">{new Date(agent.updatedAt).toLocaleString()}</dd>
              </div>
              {agent.tags.length > 0 && (
                <div>
                  <dt className="text-sm font-medium text-uw-text-secondary mb-2">Tags</dt>
                  <dd className="flex gap-2 flex-wrap">
                    {agent.tags.map((tag, i) => (
                      <span
                        key={i}
                        className="px-2 py-1 bg-uw-surface-elevated text-uw-text-secondary rounded text-xs"
                      >
                        {tag}
                      </span>
                    ))}
                  </dd>
                </div>
              )}
            </dl>
          </div>

          {agent.modelConfig && (
            <div className="bg-uw-surface-default rounded-lg shadow border border-uw-border-default p-6">
              <h2 className="text-xl font-semibold text-uw-text-primary mb-4">Model Configuration</h2>
              <dl className="space-y-3">
                <div>
                  <dt className="text-sm font-medium text-uw-text-secondary">Model</dt>
                  <dd className="text-sm text-uw-text-primary font-mono">{agent.modelConfig.model}</dd>
                </div>
                {agent.modelConfig.temperature !== undefined && (
                  <div>
                    <dt className="text-sm font-medium text-uw-text-secondary">Temperature</dt>
                    <dd className="text-sm text-uw-text-primary">{agent.modelConfig.temperature}</dd>
                  </div>
                )}
                {agent.modelConfig.maxOutputTokens && (
                  <div>
                    <dt className="text-sm font-medium text-uw-text-secondary">Max Output Tokens</dt>
                    <dd className="text-sm text-uw-text-primary">{agent.modelConfig.maxOutputTokens}</dd>
                  </div>
                )}
                {agent.modelConfig.topP !== undefined && (
                  <div>
                    <dt className="text-sm font-medium text-uw-text-secondary">Top P</dt>
                    <dd className="text-sm text-uw-text-primary">{agent.modelConfig.topP}</dd>
                  </div>
                )}
                {agent.modelConfig.topK !== undefined && (
                  <div>
                    <dt className="text-sm font-medium text-uw-text-secondary">Top K</dt>
                    <dd className="text-sm text-uw-text-primary">{agent.modelConfig.topK}</dd>
                  </div>
                )}
              </dl>
            </div>
          )}
        </div>
      )}

      {activeTab === 'dependencies' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-uw-surface-default rounded-lg shadow border border-uw-border-default p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-uw-text-primary">Dependencies</h2>
              <span className="text-sm text-uw-text-secondary">{dependencies.length} agents</span>
            </div>
            {dependencies.length === 0 ? (
              <p className="text-uw-text-secondary">No dependencies</p>
            ) : (
              <div className="space-y-2">
                {dependencies.map((dep) => (
                  <Link
                    key={dep.id}
                    to={`/ai/${dep.id}`}
                    className="block p-3 bg-uw-surface-elevated rounded-md hover:bg-uw-surface-hover transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-uw-text-primary font-medium">{dep.name}</span>
                      <span className="text-xs px-2 py-1 bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200 rounded">
                        {dep.type}
                      </span>
                    </div>
                    <p className="text-sm text-uw-text-secondary mt-1">{dep.functionName}</p>
                  </Link>
                ))}
              </div>
            )}
          </div>

          <div className="bg-uw-surface-default rounded-lg shadow border border-uw-border-default p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-uw-text-primary">Dependents</h2>
              <span className="text-sm text-uw-text-secondary">{dependents.length} agents</span>
            </div>
            {dependents.length === 0 ? (
              <p className="text-uw-text-secondary">No dependents</p>
            ) : (
              <div className="space-y-2">
                {dependents.map((dep) => (
                  <Link
                    key={dep.id}
                    to={`/ai/${dep.id}`}
                    className="block p-3 bg-uw-surface-elevated rounded-md hover:bg-uw-surface-hover transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-uw-text-primary font-medium">{dep.name}</span>
                      <span className="text-xs px-2 py-1 bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200 rounded">
                        {dep.type}
                      </span>
                    </div>
                    <p className="text-sm text-uw-text-secondary mt-1">{dep.functionName}</p>
                  </Link>
                ))}
              </div>
            )}
          </div>

          <div className="lg:col-span-2">
            <Link
              to={`/graph?center=${id}`}
              className="inline-flex items-center px-6 py-3 bg-uw-interactive-primary text-white rounded-md hover:bg-uw-interactive-primary-hover transition-colors"
            >
              <GitBranch className="w-5 h-5 mr-2" />
              View Dependency Graph
            </Link>
          </div>
        </div>
      )}

      {activeTab === 'prompts' && (
        <div className="bg-uw-surface-default rounded-lg shadow border border-uw-border-default p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-uw-text-primary">Prompts</h2>
            <span className="text-sm text-uw-text-secondary">{prompts.length} prompts</span>
          </div>
          {prompts.length === 0 ? (
            <p className="text-uw-text-secondary">No prompts found</p>
          ) : (
            <div className="space-y-4">
              {prompts.map((prompt) => (
                <div key={prompt.id} className="p-4 bg-uw-surface-elevated rounded-md">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h3 className="font-semibold text-uw-text-primary">{prompt.name}</h3>
                      <p className="text-sm text-uw-text-secondary">Category: {prompt.category}</p>
                    </div>
                    <span className="text-xs text-uw-text-tertiary">v{prompt.version}</span>
                  </div>
                  <pre className="text-sm bg-uw-surface-default p-3 rounded border border-uw-border-default overflow-x-auto text-uw-text-secondary">
                    {prompt.template}
                  </pre>
                  {prompt.variables.length > 0 && (
                    <div className="mt-2">
                      <p className="text-sm text-uw-text-tertiary">Variables: {prompt.variables.join(', ')}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'deployments' && (
        <div className="bg-uw-surface-default rounded-lg shadow border border-uw-border-default p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-uw-text-primary">Deployment History</h2>
            <span className="text-sm text-uw-text-secondary">{deployments.length} deployments</span>
          </div>
          {deployments.length === 0 ? (
            <p className="text-uw-text-secondary">No deployments found</p>
          ) : (
            <div className="space-y-3">
              {deployments.map((deployment) => (
                <div key={deployment.id} className="p-4 bg-uw-surface-elevated rounded-md">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      {getStatusIcon(deployment.status)}
                      <div>
                        <p className="font-medium text-uw-text-primary">Version {deployment.version}</p>
                        <p className="text-sm text-uw-text-secondary">
                          {new Date(deployment.deployedAt).toLocaleString()} by {deployment.deployedBy}
                        </p>
                      </div>
                    </div>
                    <span className={`px-2 py-1 text-xs font-medium rounded ${
                      deployment.status === 'success'
                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                        : deployment.status === 'failed'
                        ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                        : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                    }`}>
                      {deployment.status}
                    </span>
                  </div>
                  {deployment.error && (
                    <div className="mt-2 p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded">
                      <p className="text-sm text-red-800 dark:text-red-200">{deployment.error}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'config' && agent.modelConfig && (
        <div className="bg-uw-surface-default rounded-lg shadow border border-uw-border-default p-6">
          <h2 className="text-xl font-semibold text-uw-text-primary mb-4">Full Configuration</h2>
          <pre className="bg-uw-surface-elevated p-4 rounded-md overflow-x-auto text-sm text-uw-text-primary">
            {JSON.stringify(agent, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}
